/* ============================================
   VALINOR WATER — Main JavaScript
   All interactive functionality for the landing page
   ============================================ */

(function() {
  'use strict';

  // ============================================
  // CONFIG
  // ============================================
  const KLAVIYO_COMPANY_ID = 'Yv4JvP';
  const KLAVIYO_LIST_ID = 'ScQL6m';

  // ============================================
  // REVEAL ON SCROLL (IntersectionObserver)
  // ============================================
  function initRevealAnimations() {
    // If IntersectionObserver is unavailable, reveal everything immediately rather than throwing
    // (a throw here used to abort the whole DOMContentLoaded callback). Email binding now runs
    // first regardless, but this keeps the reveal path itself safe on old/locked-down browsers.
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('.valinor-reveal').forEach(function(el) { el.classList.add('visible'); });
      return;
    }
    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    // was rootMargin 300px past the bottom edge — fired the reveal while the section was
    // still below the fold, so it had already finished animating by the time it was seen.
    // Now fires once the section is actually ~12% into the viewport. Will 2026-06-30
    }, { threshold: 0.12, rootMargin: '0px 0px -12% 0px' });

    function observeAll() {
      document.querySelectorAll('.valinor-reveal:not(.visible)').forEach(function(el) {
        observer.observe(el);
      });
    }

    // Observe immediately
    observeAll();

    // Re-observe after Shopify finishes rendering sections (race condition fix)
    setTimeout(observeAll, 100);
    setTimeout(observeAll, 500);

    // Safety net for elements the observer never catches (e.g. IntersectionObserver failing).
    // Was 3s — that's shorter than most visitors' hero dwell time, so below-the-fold sections
    // were silently self-revealing before anyone scrolled to them, making the reveal invisible
    // in practice. Bumped well past normal scroll behavior. Will 2026-06-30
    setTimeout(function() {
      document.querySelectorAll('.valinor-reveal:not(.visible)').forEach(function(el) {
        el.classList.add('visible');
      });
    }, 15000);

    // Store reference for re-observing dynamically added elements
    window.valinorRevealObserver = observer;
  }

  // ============================================
  // EMAIL SUBMIT (Klaviyo)
  // ============================================
  // Durable, same-origin capture to Shopify. Because this POSTs to our OWN domain (not a
  // third-party tracker host), it cannot be blocked by ad-blockers / privacy extensions, and it
  // lands the email in Shopify admin (Customers, email-subscribed) as the never-lose-it safety net.
  function captureToShopify(email, source) {
    var body = 'form_type=customer&utf8=%E2%9C%93'
      + '&contact%5Btags%5D=' + encodeURIComponent('valinor-web,' + (source || 'signup'))
      + '&contact%5Bemail%5D=' + encodeURIComponent(email);
    return fetch('/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body
    }).then(function(r) { return r.ok; }).catch(function() { return false; });
  }

  async function handleEmailSubmit(e) {
    e.preventDefault();

    // Determine which form was submitted
    const form = e.target;
    // Match the email field by any of the shapes our forms use (type="email" on the waitlist,
    // type="text" inputmode="email" on hero/shop). Hard-coding input[type="email"] silently
    // broke submit when the hero/shop inputs were switched to type="text" (Will 7/04).
    const emailInput = form.querySelector('input[type="email"], input[type="text"]');
    const email = emailInput ? emailInput.value.trim() : '';
    if (!email) return;

    // Attribute the source by which form was submitted (hero / shop / waitlist).
    // getAttribute, not .id — on a <form>, .id can be shadowed by a child <input name="id">
    // (e.g. the Shop page's cart form), returning an element instead of the id string.
    const id = form.getAttribute('id') || '';
    const source = id.indexOf('hero') !== -1 ? 'Hero'
      : id.indexOf('shop') !== -1 ? 'Shop'
      : 'Waitlist';

    // 1) Durable same-origin capture FIRST — the email is safe even if Klaviyo is blocked/down.
    const shopifyOk = await captureToShopify(email, source);

    // 2) Best-effort Klaviyo for list membership + flow automation (may be ad-blocked).
    let klaviyoOk = false;
    try {
      const subRes = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${KLAVIYO_COMPANY_ID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', 'revision': '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: source,
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email: email }
                }
              }
            },
            relationships: {
              list: {
                data: { type: 'list', id: KLAVIYO_LIST_ID }
              }
            }
          }
        })
      });
      klaviyoOk = subRes.ok;

      // Profile update rides along (best effort — never gates success).
      fetch(`https://a.klaviyo.com/client/profiles/?company_id=${KLAVIYO_COMPANY_ID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', 'revision': '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'profile',
            attributes: {
              email: email,
              properties: { 'Source': source }
            }
          }
        })
      }).catch(function() {});
    } catch (err) {
      klaviyoOk = false;
    }

    // Only fail if BOTH the durable and best-effort paths failed — i.e. the visitor is effectively
    // offline, in which case nothing could have been captured anywhere. We never fake success.
    if (!shopifyOk && !klaviyoOk) {
      let errEl = form.querySelector('.valinor-email-error');
      if (!errEl) {
        errEl = document.createElement('p');
        errEl.className = 'valinor-email-error';
        errEl.style.cssText = 'color:#b3261e;font-size:13px;margin:8px 0 0;grid-column:1/-1;';
        form.appendChild(errEl);
      }
      errEl.textContent = 'Something blocked the signup. Please try again, or email hello@valinorwater.com.';
      return;
    }

    // Show success states in ALL forms on the page
    // getAttribute, not el.id — the Shop page's cart form has an <input name="id"> that
    // shadows .id on the form element, which made this loop throw and skip the success state.
    // 'valinor-hero' dropped from both loops 2026-08-10: the homepage hero is now the numbered
    // waitlist bar, which owns its own submit and redirects to /pages/waitlist. Leaving it here
    // meant a signup in the page's OTHER form (the closing stay-informed band) hid the hero's
    // capture bar and Learn More button mid-page. Shop and waitlist are untouched.
    document.querySelectorAll('[id$="-form"]').forEach(el => {
      const elId = el.getAttribute('id') || '';
      if (elId.startsWith('valinor-waitlist') || elId.startsWith('valinor-shop-email')) {
        el.style.display = 'none';
      }
    });
    document.querySelectorAll('[id$="-success"]').forEach(el => {
      const elId = el.getAttribute('id') || '';
      if (elId.startsWith('valinor-waitlist') || elId.startsWith('valinor-shop-email')) {
        el.style.display = 'block';
      }
    });
  }

  function initEmailForms() {
    // No hero binding: the hero form is the waitlist capture bar and posts to the Worker itself.
    const waitlistForm = document.getElementById('valinor-waitlist-email-form');
    const shopForm = document.getElementById('valinor-shop-email-form');

    if (waitlistForm) waitlistForm.addEventListener('submit', handleEmailSubmit);
    if (shopForm) shopForm.addEventListener('submit', handleEmailSubmit);
  }

  // ============================================
  // SOLUTION TABLE: Toggle detail panels
  // ============================================
  window.valinorToggleVsDetail = function(btn) {
    const mainRow = btn.closest('tr');
    const detailRow = mainRow.nextElementSibling;
    const panel = detailRow.querySelector('.valinor-vs-detail');
    const arrow = btn.querySelector('.valinor-why-arrow');
    const isOpen = panel.style.maxHeight && panel.style.maxHeight !== '0px';

    // Close all panels
    document.querySelectorAll('.valinor-vs-detail').forEach(p => p.style.maxHeight = '0px');
    document.querySelectorAll('.valinor-why-arrow').forEach(a => a.style.transform = 'none');

    // Open this one if it was closed
    if (!isOpen) {
      panel.style.maxHeight = panel.scrollHeight + 'px';
      arrow.style.transform = 'rotate(90deg)';
    }
  };

  // ============================================
  // COLLAPSIBLE SECTIONS
  // ============================================
  window.valinorToggleCollapsible = function(id, toggleEl) {
    const el = document.getElementById(id);
    if (!el) return;
    const icon = toggleEl.querySelector('.valinor-toggle-icon');
    el.classList.toggle('open');
    if (icon) icon.classList.toggle('open');

    // Re-observe any newly revealed elements
    if (el.classList.contains('open') && window.valinorRevealObserver) {
      el.querySelectorAll('.valinor-reveal:not(.visible)').forEach(r => {
        window.valinorRevealObserver.observe(r);
      });
    }
  };

  // ============================================
  // MOBILE COMPARISON WIDGET
  // ============================================
  const compData = {
    valinor: { name: "VALINOR", contaminants: "75+", vessel: "Glass", housing: "Stainless Steel", speed: "~2.5", pfas: "✓", microplastics: "✓", pharma: "✓", usa: "✓" },
    brita_std: { name: "Brita Standard", contaminants: "5", vessel: "Plastic", housing: "Plastic", speed: "~3.5", pfas: "✕", microplastics: "✕", pharma: "✕", usa: "✕" },
    pur_std: { name: "PUR Standard", contaminants: "12", vessel: "Plastic", housing: "Plastic", speed: "~5.0", pfas: "✕", microplastics: "✕", pharma: "✕", usa: "✕" },
    brita_elite: { name: "Brita Elite", contaminants: "32", vessel: "Plastic", housing: "Plastic", speed: "~5.4", pfas: "Some", microplastics: "✕", pharma: "Some", usa: "✕" },
    pur_plus: { name: "PUR Plus", contaminants: "21", vessel: "Plastic", housing: "Plastic", speed: "~5.6", pfas: "Some", microplastics: "Some", pharma: "Some", usa: "✕" },
    zerowater: { name: "ZeroWater", contaminants: "28", vessel: "Plastic", housing: "Plastic", speed: "~8.3", pfas: "✓", microplastics: "✕", pharma: "Some", usa: "✕" },
    larq: { name: "LARQ*", contaminants: "58", vessel: "Plastic", housing: "Plastic", speed: "~26", pfas: "Some", microplastics: "Some", pharma: "Some", usa: "✕" },
    aarke: { name: "Aarke", contaminants: "3", vessel: "Glass", housing: "Stainless Steel", speed: "~4.0", pfas: "✕", microplastics: "✕", pharma: "✕", usa: "✕" },
  };

  const compFeatures = [
    { key: "contaminants", label: "Contaminants reduced" },
    { key: "vessel", label: "Pitcher material" },
    { key: "housing", label: "Filter housing" },
    { key: "speed", label: "Speed (min/L)" },
    { key: "pfas", label: "PFAS" },
    { key: "microplastics", label: "Microplastics" },
    { key: "pharma", label: "Pharmaceuticals" },
    { key: "usa", label: "Filters made in USA" },
  ];

  window.valinorUpdateMobileComparison = function() {
    const sel = document.getElementById('valinor-comp-select');
    if (!sel) return;
    const comp = compData[sel.value];
    const v = compData.valinor;
    const container = document.getElementById('valinor-mobile-comp-table');
    if (!container) return;

    let html = '<table style="width: 100%; border-collapse: collapse; font-size: 14px;">';
    html += '<thead><tr style="border-bottom: 2px solid #ddd;">';
    html += '<th style="text-align: left; padding: 12px 8px; font-weight: 400; color: #999; width: 40%;"></th>';
    html += '<th style="padding: 12px 8px; font-weight: 700; color: #fff; background: var(--valinor-teal); border-radius: 12px 12px 0 0; width: 30%; text-align: center; font-size: 13px; letter-spacing: 0.5px;">VALINOR</th>';
    html += '<th style="padding: 12px 8px; font-weight: 500; color: #888; width: 30%; text-align: center; font-size: 13px;">' + comp.name + '</th>';
    html += '</tr></thead><tbody>';

    compFeatures.forEach(function(f, i) {
      const isLast = i === compFeatures.length - 1;
      const vVal = v[f.key];
      const cVal = comp[f.key];
      const vStyle = 'padding: 14px 8px; background: var(--valinor-teal); color: #fff; font-weight: 600; text-align: center; border-bottom: 1px solid rgba(255,255,255,0.15);' + (isLast ? ' border-radius: 0 0 12px 12px;' : '');
      const cColor = cVal === "✕" ? "#ccc" : (cVal === "Some" ? "#aaa" : "#666");

      html += '<tr style="border-bottom: 1px solid #eee;">';
      html += '<td style="padding: 14px 8px; color: #444; font-size: 13px;">' + f.label + '</td>';
      html += '<td style="' + vStyle + '">' + vVal + '</td>';
      html += '<td style="padding: 14px 8px; color: ' + cColor + '; text-align: center;">' + cVal + '</td>';
      html += '</tr>';
    });

    html += '</tbody></table>';
    container.innerHTML = html;
  };

  // ============================================
  // BAR CHART (Contaminant Comparison)
  // ============================================
  function buildBarChart() {
    const barChart = document.getElementById('valinor-bar-chart');
    if (!barChart) return;

    const bars = [
      { brand: "Valinor", count: 75, label: "75+", highlight: true },
      { brand: "Brita Elite", count: 32 },
      { brand: "ZeroWater", count: 28 },
      { brand: "PUR Plus", count: 21 },
      { brand: "PUR Standard", count: 12 },
      { brand: "Brita Standard", count: 5 },
      { brand: "Aarke", count: 3 },
    ];

    var prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    barChart.classList.add('valinor-barchart');
    bars.forEach(function(bar, i) {
      const pct = (bar.count / 75) * 100;
      const row = document.createElement('div');
      row.className = 'valinor-barrow' + (bar.highlight ? ' is-valinor' : '');
      row.innerHTML =
        '<p class="valinor-barrow-brand">' + bar.brand + '</p>' +
        '<div class="valinor-bartrack">' +
          (bar.highlight ? '<span class="valinor-barghost" aria-hidden="true">' + bar.count + '</span>' : '') +
          '<div class="valinor-barfill" style="--target: ' + pct + '%; transition-delay: ' + (i * 90) + 'ms;"></div>' +
        '</div>' +
        '<span class="valinor-barnum" data-count="' + bar.count + '"' + (bar.highlight ? ' data-suffix="+"' : '') + '>' + (prefersReduced ? (bar.highlight ? (bar.label || bar.count) : bar.count) : '0') + '</span>';
      barChart.appendChild(row);
    });

    function countBarNums() {
      if (prefersReduced) return;
      barChart.querySelectorAll('.valinor-barnum').forEach(function(el) {
        var target = parseInt(el.getAttribute('data-count'), 10);
        var suffix = el.getAttribute('data-suffix') || '';
        var start = null, dur = 1100;
        function step(ts) {
          if (start === null) start = ts;
          var p = Math.min(1, (ts - start) / dur);
          var eased = 1 - Math.pow(1 - p, 3);
          el.textContent = Math.round(target * eased) + (p >= 1 ? suffix : '');
          if (p < 1) requestAnimationFrame(step);
          else el.textContent = target + suffix;
        }
        requestAnimationFrame(step);
      });
    }

    // Animate the bars + count the numbers up when the chart scrolls into view.
    function reveal() { barChart.classList.add('is-in'); countBarNums(); }
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function(entries) {
        entries.forEach(function(e) {
          if (e.isIntersecting) { reveal(); io.disconnect(); }
        });
      }, { threshold: 0.3 });
      io.observe(barChart);
    } else {
      reveal();
    }
  }

  // ============================================
  // SCIENCE STATS — removed (no #valinor-science-stats container on homepage)

  // ============================================
  // STICKY BAR
  // ============================================
  function initStickyBar() {
    const stickyBar = document.getElementById('valinor-sticky-bar');
    if (!stickyBar) return;

    window.addEventListener('scroll', function() {
      stickyBar.classList.toggle('show', window.scrollY > 600);
    });
  }

  // ============================================
  // INIT
  // ============================================
  document.addEventListener('DOMContentLoaded', function() {
    // Bind email forms FIRST so nothing that could throw in the reveal/animation code can ever
    // run before the email handlers are attached. Email capture is the priority path.
    initEmailForms();
    initRevealAnimations();
    buildBarChart();
    initStickyBar();

    // Initialize mobile comparison widget
    if (document.getElementById('valinor-comp-select')) {
      window.valinorUpdateMobileComparison();
    }
  });

})();
